// useMediaQuery — minimal subscribe-to-matchMedia hook.
//
// Returns true if the current viewport matches the passed query. Reactive:
// re-renders when the match state flips (resize, orientation change, etc.).
// SSR-safe: returns false on the server / before window exists.
//
// Atlas mobile breakpoints (per atlas-mobile-DESIGN.md §2):
//   useMediaQuery('(max-width: 1023px)') → mobile + tablet
//   useMediaQuery('(max-width: 767px)')  → mobile only
//   useMediaQuery('(max-width: 360px)')  → primary mobile design canvas

import { useState, useEffect } from 'react'

export function useMediaQuery(query) {
  const get = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false

  const [matches, setMatches] = useState(get)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia(query)
    const handler = (e) => setMatches(e.matches)
    // Match initial state in case it changed between render and mount.
    setMatches(mql.matches)
    // addEventListener is the modern API; addListener is the deprecated
    // fallback for older Safari.
    if (mql.addEventListener) {
      mql.addEventListener('change', handler)
      return () => mql.removeEventListener('change', handler)
    } else {
      mql.addListener(handler)
      return () => mql.removeListener(handler)
    }
  }, [query])

  return matches
}

// Convenience wrappers matching the DESIGN.md breakpoints.
export const useIsMobile = () => useMediaQuery('(max-width: 1023px)')
export const useIsPhone = () => useMediaQuery('(max-width: 767px)')
